import { createFileRoute } from '@tanstack/react-router';
import { useCallback, useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Landmark } from 'lucide-react';
import { cn } from '@siliconx/shared/ui/utils';
import { Button } from '@siliconx/shared/ui/Button';
import { Card } from '@siliconx/shared/ui/Card';
import { PageHeader } from '@siliconx/shared/ui/PageHeader';
import { QueryBoundary } from '@siliconx/shared/ui/QueryBoundary';
import { Field, TextInput } from '@siliconx/shared/ui/Field';
import type {
  CapitalizationPolicyTreeResponse,
  ClassPolicyTreeEntry,
  DepreciationMethod,
  ErpPolicySuggestionClass,
  ErpPolicySuggestionResponse,
  Phase1DepreciationMethod,
  PolicyTier,
  ResolvedPolicy,
} from '@siliconx/shared';
import { useAuth } from '../../lib/authContext';
import { api, throwIfNotOk } from '../../lib/api';
import { PolicySuggestionBanner } from '../../components/financials/PolicySuggestionBanner';
import { qk } from '../../lib/queryKeys';

/**
 * SIL-251 T15 — Capitalization policy screen (org default + class tabs).
 * Spec §5, §7 Wave 4. Backs `GET/PUT/DELETE /api/client/financials/policy*`
 * (T8, apps/api/src/routes/client/financials-policy.ts) — three-tier
 * resolution: per-class override -> org default -> platform default.
 *
 * Read is open to any member; write controls (save org default, save/reset a
 * class override) are admin-only. The server is authoritative (RLS + role
 * check on financials-policy.ts) — this screen only hides/disables controls
 * for non-admins, mirroring routes/team.tsx.
 */
export const Route = createFileRoute('/financials/policy')({
  component: CapitalizationPolicyPage,
});

// ---------------------------------------------------------------------------
// Form state — mirrors orgDefaultPolicyWriteSchema / classOverridePolicyWriteSchema
// (packages/shared/src/dto/financials.ts). fiscalYearStartMonth only applies
// to the org-default row (per-class rows ignore it, T8).
// ---------------------------------------------------------------------------

interface PolicyFormState {
  method: Phase1DepreciationMethod;
  usefulLifeYears: number;
  dbFactor: number | null;
  salvagePct: number;
  capThresholdUsd: number;
  fiscalYearStartMonth: number;
}

const EMPTY_FORM: PolicyFormState = {
  method: 'straight_line',
  usefulLifeYears: 5,
  dbFactor: null,
  salvagePct: 0,
  capThresholdUsd: 0,
  fiscalYearStartMonth: 1,
};

function toFormState(row: {
  method: DepreciationMethod;
  usefulLifeYears: number;
  dbFactor: number | null;
  salvagePct: number;
  capThresholdUsd: number;
  fiscalYearStartMonth?: number;
}): PolicyFormState {
  return {
    // sum_of_years_digits is read-only-only (deferred out of phase-1 writes,
    // spec §2.2) — a resolved/stored row can carry it, but the write form
    // never offers it as an option, so fall back to straight_line if seen.
    method: row.method === 'declining_balance' ? 'declining_balance' : 'straight_line',
    usefulLifeYears: row.usefulLifeYears,
    dbFactor: row.dbFactor,
    salvagePct: row.salvagePct,
    capThresholdUsd: row.capThresholdUsd,
    fiscalYearStartMonth: row.fiscalYearStartMonth ?? 1,
  };
}

function tierBadgeCls(tier: 'override' | 'inherits') {
  return cn(
    'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium',
    tier === 'override' ? 'bg-brand-100 text-brand-700' : 'bg-muted text-muted-foreground',
  );
}

// ---------------------------------------------------------------------------
// SOURCE badge (SIL-306, W4) — per-class corroboration signal from the
// EXISTING policy-suggestions endpoint (`GET /financials/policy/suggestions`,
// SIL-269) that already backs `PolicySuggestionBanner`; no new endpoint. A
// class with a non-empty ERP sample that agrees with the configured policy
// reads "ERP CONFIRMED"; a disagreeing sample reads "ERP DIFFERS"; a class
// with no ERP-reported facts yet falls back to its resolved tier alone.
// ---------------------------------------------------------------------------

const POLICY_TIER_LABELS: Record<PolicyTier, string> = {
  class_override: 'Class override',
  org_default: 'Org default',
  platform_default: 'Platform default',
  no_policy: 'No policy',
};

function SourceBadge({ suggestion, tier }: { suggestion: ErpPolicySuggestionClass | undefined; tier: PolicyTier }) {
  const hasSample = suggestion != null && suggestion.sampleSize > 0;
  const disagrees = hasSample && (suggestion.disagreesOnMethod || suggestion.disagreesOnLife);

  return (
    <span className="flex items-center gap-1">
      {hasSample && (
        <span
          className={cn(
            'inline-flex items-center rounded-full px-1.5 py-0.5 text-xs font-semibold uppercase tracking-wide',
            disagrees ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700',
          )}
        >
          {disagrees ? 'ERP differs' : 'ERP confirmed'}
        </span>
      )}
      <span className="text-xs uppercase tracking-wide text-muted-foreground">{POLICY_TIER_LABELS[tier]}</span>
    </span>
  );
}

const MONTH_LABELS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

// ---------------------------------------------------------------------------
// Toast (mirrors routes/team.tsx)
// ---------------------------------------------------------------------------

function Toast({
  message,
  type,
  onDismiss,
}: {
  message: string;
  type: 'success' | 'error';
  onDismiss: () => void;
}) {
  useEffect(() => {
    const t = setTimeout(onDismiss, 4000);
    return () => clearTimeout(t);
  }, [onDismiss]);
  return (
    <div
      className={cn(
        'fixed bottom-6 right-6 z-50 rounded-lg px-4 py-3 text-sm font-medium shadow-lg',
        type === 'success' ? 'bg-green-600 text-white' : 'bg-red-600 text-white',
      )}
    >
      {message}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Derived-rate readout — annualRate is server-computed, never editable
// (resolvedPolicySchema.annualRate, spec §3.1).
// ---------------------------------------------------------------------------

function DerivedRateReadout({ resolved }: { resolved: ResolvedPolicy | null }) {
  return (
    <div className="rounded-md border border-border bg-muted px-3 py-2">
      <div className="text-xs font-medium text-muted-foreground">Derived annual depreciation rate</div>
      <div className="text-sm font-semibold text-foreground">
        {resolved?.annualRate != null ? `${(resolved.annualRate * 100).toFixed(2)}%` : '—'}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Editable policy form — used for org-default (fiscalYearStartMonth shown)
// and per-class overrides (fiscalYearStartMonth hidden, spec §2.2).
// ---------------------------------------------------------------------------

function PolicyForm({
  form,
  onChange,
  showFiscalYearStart,
  isAdmin,
  onSubmit,
  submitLabel,
  saving,
  resolved,
  extraAction,
}: {
  form: PolicyFormState;
  onChange: (next: PolicyFormState) => void;
  showFiscalYearStart: boolean;
  isAdmin: boolean;
  onSubmit: () => void;
  submitLabel: string;
  saving: boolean;
  resolved: ResolvedPolicy | null;
  extraAction?: React.ReactNode;
}) {
  const isDb = form.method === 'declining_balance';
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit();
      }}
      className="space-y-4"
    >
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field label="Method">
          <select
            value={form.method}
            disabled={!isAdmin}
            onChange={(e) =>
              onChange({ ...form, method: e.target.value as Phase1DepreciationMethod })
            }
            className="w-full rounded-md border border-border px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 disabled:bg-muted disabled:text-muted-foreground"
          >
            <option value="straight_line">Straight-line</option>
            <option value="declining_balance">Declining-balance</option>
          </select>
        </Field>

        <Field label="Useful life (years)">
          <TextInput
            type="number"
            min={1}
            max={30}
            value={form.usefulLifeYears}
            disabled={!isAdmin}
            onChange={(e) => onChange({ ...form, usefulLifeYears: Number(e.target.value) })}
          />
        </Field>

        <Field label={isDb ? 'DB factor' : 'DB factor (declining-balance only)'}>
          <input
            type="number"
            min={0}
            step="0.1"
            value={form.dbFactor ?? ''}
            disabled={!isAdmin || !isDb}
            onChange={(e) =>
              onChange({ ...form, dbFactor: e.target.value === '' ? null : Number(e.target.value) })
            }
            placeholder={isDb ? 'e.g. 2.0' : '—'}
            className={cn(
              'w-full rounded-md border px-3 py-2 text-sm focus:outline-none disabled:text-muted-foreground',
              isDb
                ? 'border-border focus:border-brand-500 focus:ring-1 focus:ring-brand-500 disabled:bg-muted'
                : 'border-border bg-muted',
            )}
          />
        </Field>

        <Field label="Salvage value (%)">
          <TextInput
            type="number"
            min={0}
            max={100}
            step="0.01"
            value={form.salvagePct}
            disabled={!isAdmin}
            onChange={(e) => onChange({ ...form, salvagePct: Number(e.target.value) })}
          />
        </Field>

        <Field label="Capitalization threshold (USD)">
          <TextInput
            type="number"
            min={0}
            step="0.01"
            value={form.capThresholdUsd}
            disabled={!isAdmin}
            onChange={(e) => onChange({ ...form, capThresholdUsd: Number(e.target.value) })}
          />
        </Field>

        {showFiscalYearStart && (
          <Field label="Fiscal year start month">
            <select
              value={form.fiscalYearStartMonth}
              disabled={!isAdmin}
              onChange={(e) => onChange({ ...form, fiscalYearStartMonth: Number(e.target.value) })}
              className="w-full rounded-md border border-border px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 disabled:bg-muted disabled:text-muted-foreground"
            >
              {MONTH_LABELS.map((label, i) => (
                <option key={label} value={i + 1}>{label}</option>
              ))}
            </select>
          </Field>
        )}
      </div>

      <DerivedRateReadout resolved={resolved} />

      {isAdmin && (
        <div className="flex items-center gap-2 pt-1">
          <Button
            type="submit"
            disabled={saving}
          >
            {saving ? 'Saving…' : submitLabel}
          </Button>
          {extraAction}
        </div>
      )}
    </form>
  );
}

// ---------------------------------------------------------------------------
// Read-only resolved-policy display — inheriting classes (spec §5).
// ---------------------------------------------------------------------------

function ResolvedPolicyReadOnly({ resolved }: { resolved: ResolvedPolicy }) {
  const rows: Array<[string, string]> = [
    ['Method', resolved.method === 'declining_balance' ? 'Declining-balance' : resolved.method === 'straight_line' ? 'Straight-line' : resolved.method === 'sum_of_years_digits' ? 'Sum-of-years-digits' : '—'],
    ['Useful life (years)', resolved.usefulLifeYears != null ? String(resolved.usefulLifeYears) : '—'],
    ['DB factor', resolved.dbFactor != null ? String(resolved.dbFactor) : '—'],
    ['Salvage value (%)', resolved.salvagePct != null ? String(resolved.salvagePct) : '—'],
  ];
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        {rows.map(([label, value]) => (
          <div key={label}>
            <div className="mb-1 text-xs font-medium text-muted-foreground">{label}</div>
            <div className="rounded-md border border-border bg-muted px-3 py-2 text-sm text-foreground">
              {value}
            </div>
          </div>
        ))}
      </div>
      <DerivedRateReadout resolved={resolved} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Empty state — no org default has ever been set (spec §5).
// ---------------------------------------------------------------------------

function EmptyOrgDefault({ isAdmin, onCreate }: { isAdmin: boolean; onCreate: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-border bg-white px-6 py-16 text-center">
      <Landmark className="mb-3 size-8 text-muted-foreground" />
      <p className="text-sm font-medium text-foreground">No organization default policy set</p>
      <p className="mt-1 max-w-sm text-xs text-muted-foreground">
        Every asset class falls back to the platform default until an org-wide capitalization
        policy is defined.
      </p>
      {isAdmin && (
        <Button className="mt-4"
          onClick={onCreate}
        >
          Set organization default
        </Button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

type TabSelection = { kind: 'org-default' } | { kind: 'class'; assetClassId: string };

function CapitalizationPolicyPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const queryClient = useQueryClient();

  const treeQ = useQuery<CapitalizationPolicyTreeResponse>({
    queryKey: qk.financials.policy(),
    queryFn: async () => {
      const res = await api.api.client.financials.policy.$get();
      await throwIfNotOk(res);
      return res.json() as Promise<CapitalizationPolicyTreeResponse>;
    },
  });
  const treeData = treeQ.data ?? null;
  const reload = useCallback(
    () => void queryClient.invalidateQueries({ queryKey: qk.financials.policy() }),
    [queryClient],
  );

  const [tab, setTab] = useState<TabSelection>({ kind: 'org-default' });
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [overriding, setOverriding] = useState(false); // "Override for this class" edit mode
  const [creatingOrgDefault, setCreatingOrgDefault] = useState(false); // "Set organization default" edit mode

  const notify = useCallback((message: string, type: 'success' | 'error') => setToast({ message, type }), []);

  // SIL-306 (W4) — reuses the SAME suggestions endpoint PolicySuggestionBanner
  // already queries (SIL-269), just for the per-class SOURCE badge rather
  // than the disagreement banner. No new endpoint.
  const suggestionsQ = useQuery<ErpPolicySuggestionResponse>({
    queryKey: qk.financials.policySuggestions(),
    queryFn: async () => {
      const res = await api.api.client.financials.policy.suggestions.$get();
      await throwIfNotOk(res);
      return res.json();
    },
  });
  const suggestionByClass = new Map(
    (suggestionsQ.data?.classes ?? []).map((s) => [s.assetClassId, s]),
  );

  useEffect(() => {
    setOverriding(false);
    setCreatingOrgDefault(false);
  }, [tab]);

  // Org-default form state, seeded from the loaded row (or EMPTY_FORM when
  // showing the "Set organization default" empty state).
  const [orgForm, setOrgForm] = useState<PolicyFormState>(EMPTY_FORM);
  useEffect(() => {
    if (treeData?.orgDefault) setOrgForm(toFormState(treeData.orgDefault));
  }, [treeData?.orgDefault]);

  // Per-class override form state, keyed by the currently-selected tab.
  const [classForm, setClassForm] = useState<PolicyFormState>(EMPTY_FORM);
  const selectedClass: ClassPolicyTreeEntry | null =
    tab.kind === 'class' ? treeData?.classes.find((c) => c.assetClassId === tab.assetClassId) ?? null : null;

  useEffect(() => {
    if (!selectedClass) return;
    if (selectedClass.override) {
      setClassForm(toFormState(selectedClass.override));
    } else if (selectedClass.resolved.method) {
      // Prefill "Override for this class" from the currently-resolved policy.
      setClassForm(
        toFormState({
          method: selectedClass.resolved.method,
          usefulLifeYears: selectedClass.resolved.usefulLifeYears ?? EMPTY_FORM.usefulLifeYears,
          dbFactor: selectedClass.resolved.dbFactor,
          salvagePct: selectedClass.resolved.salvagePct ?? EMPTY_FORM.salvagePct,
          capThresholdUsd: EMPTY_FORM.capThresholdUsd,
        }),
      );
    } else {
      setClassForm(EMPTY_FORM);
    }
  }, [selectedClass]);

  async function saveOrgDefault() {
    setSaving(true);
    try {
      const res = await api.api.client.financials.policy['org-default'].$put({
        json: {
          method: orgForm.method,
          usefulLifeYears: orgForm.usefulLifeYears,
          dbFactor: orgForm.method === 'declining_balance' ? orgForm.dbFactor : null,
          salvagePct: orgForm.salvagePct,
          capThresholdUsd: orgForm.capThresholdUsd,
          fiscalYearStartMonth: orgForm.fiscalYearStartMonth,
        },
      });
      if (!res.ok) {
        await throwIfNotOk(res);
        throw new Error(`HTTP ${res.status}`);
      }
      notify('Organization default policy saved.', 'success');
      setCreatingOrgDefault(false);
      reload();
    } catch (ex: unknown) {
      notify((ex as Error).message, 'error');
    } finally {
      setSaving(false);
    }
  }

  async function saveClassOverride(assetClassId: string) {
    setSaving(true);
    try {
      const res = await api.api.client.financials.policy.class[':assetClassId'].$put({
        param: { assetClassId },
        json: {
          method: classForm.method,
          usefulLifeYears: classForm.usefulLifeYears,
          dbFactor: classForm.method === 'declining_balance' ? classForm.dbFactor : null,
          salvagePct: classForm.salvagePct,
          capThresholdUsd: classForm.capThresholdUsd,
        },
      });
      if (!res.ok) {
        await throwIfNotOk(res);
        throw new Error(`HTTP ${res.status}`);
      }
      notify('Class override saved.', 'success');
      setOverriding(false);
      reload();
    } catch (ex: unknown) {
      notify((ex as Error).message, 'error');
    } finally {
      setSaving(false);
    }
  }

  async function resetClassOverride(assetClassId: string) {
    if (!confirm('Reset this class to the organization default policy?')) return;
    setSaving(true);
    try {
      const res = await api.api.client.financials.policy.class[':assetClassId'].$delete({
        param: { assetClassId },
      });
      if (!res.ok) {
        await throwIfNotOk(res);
        throw new Error(`HTTP ${res.status}`);
      }
      notify('Class reset to organization default.', 'success');
      reload();
    } catch (ex: unknown) {
      notify((ex as Error).message, 'error');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="space-y-6">
      <div>
        <PageHeader title="Capitalization Policy" pinTo="/financials/policy" />
        <p className="mt-1 text-sm text-muted-foreground">
          Sets how assets depreciate for book-value reporting. Every asset class resolves
          through a class override, then the organization default, then the platform default.
        </p>
      </div>

      <QueryBoundary query={treeQ} errorTitle="Couldn't load capitalization policy.">
        {(tree) => (
          <>
            <PolicySuggestionBanner isAdmin={isAdmin} onApplied={reload} />

            <div className="flex flex-col gap-6 lg:flex-row">
              {/* Left panel — org default first, then class tabs badged inherits/override. */}
              <aside className="w-full shrink-0 lg:w-64">
                <div className="rounded-lg border border-border bg-card">
                  <button
                    type="button"
                    onClick={() => setTab({ kind: 'org-default' })}
                    className={cn(
                      'flex w-full items-center justify-between border-b border-border px-4 py-3 text-left text-sm font-medium',
                      tab.kind === 'org-default' ? 'bg-brand-50 text-brand-700' : 'text-foreground hover:bg-muted',
                    )}
                  >
                    Organization default
                  </button>
                  <nav className="max-h-[60vh] overflow-y-auto">
                    {tree.classes.map((c) => {
                      const active = tab.kind === 'class' && tab.assetClassId === c.assetClassId;
                      const hasOverride = c.override !== null;
                      return (
                        <button
                          key={c.assetClassId}
                          type="button"
                          onClick={() => setTab({ kind: 'class', assetClassId: c.assetClassId })}
                          className={cn(
                            'flex w-full flex-col gap-1 border-b border-border px-4 py-2.5 text-left text-sm',
                            active ? 'bg-brand-50 font-medium text-brand-700' : 'text-foreground hover:bg-muted',
                          )}
                        >
                          <span className="flex items-center justify-between gap-2">
                            <span className="truncate">{c.assetClassName}</span>
                            <span className={tierBadgeCls(hasOverride ? 'override' : 'inherits')}>
                              {hasOverride ? 'override' : 'inherits'}
                            </span>
                          </span>
                          <SourceBadge suggestion={suggestionByClass.get(c.assetClassId)} tier={c.resolved.tier} />
                        </button>
                      );
                    })}
                  </nav>
                </div>
              </aside>

              {/* Right panel — selected tab's form / read-only view. */}
              <Card as="section" className="min-w-0 flex-1">
                {tab.kind === 'org-default' ? (
                  tree.orgDefault || creatingOrgDefault ? (
                    <PolicyForm
                      form={orgForm}
                      onChange={setOrgForm}
                      showFiscalYearStart
                      isAdmin={isAdmin}
                      onSubmit={saveOrgDefault}
                      submitLabel={tree.orgDefault ? 'Save organization default' : 'Create organization default'}
                      saving={saving}
                      resolved={tree.orgDefaultResolved}
                      extraAction={
                        !tree.orgDefault ? (
                          <button
                            type="button"
                            onClick={() => setCreatingOrgDefault(false)}
                            className="rounded-md px-4 py-2 text-sm font-medium text-muted-foreground hover:bg-muted"
                          >
                            Cancel
                          </button>
                        ) : undefined
                      }
                    />
                  ) : (
                    <EmptyOrgDefault
                      isAdmin={isAdmin}
                      onCreate={() => {
                        setOrgForm(EMPTY_FORM);
                        setCreatingOrgDefault(true);
                      }}
                    />
                  )
                ) : selectedClass ? (
                  selectedClass.override || overriding ? (
                    <PolicyForm
                      form={classForm}
                      onChange={setClassForm}
                      showFiscalYearStart={false}
                      isAdmin={isAdmin}
                      onSubmit={() => saveClassOverride(selectedClass.assetClassId)}
                      submitLabel={selectedClass.override ? 'Save override' : 'Create override'}
                      saving={saving}
                      resolved={selectedClass.resolved}
                      extraAction={
                        selectedClass.override ? (
                          <button
                            type="button"
                            disabled={saving}
                            onClick={() => resetClassOverride(selectedClass.assetClassId)}
                            className="rounded-md border border-border px-4 py-2 text-sm font-medium text-muted-foreground hover:bg-muted disabled:opacity-50"
                          >
                            Reset to org default
                          </button>
                        ) : (
                          <button
                            type="button"
                            onClick={() => setOverriding(false)}
                            className="rounded-md px-4 py-2 text-sm font-medium text-muted-foreground hover:bg-muted"
                          >
                            Cancel
                          </button>
                        )
                      }
                    />
                  ) : (
                    <div className="space-y-4">
                      <ResolvedPolicyReadOnly resolved={selectedClass.resolved} />
                      {isAdmin && (
                        <button
                          type="button"
                          onClick={() => setOverriding(true)}
                          className="rounded-md border border-border px-4 py-2 text-sm font-medium text-foreground hover:bg-muted"
                        >
                          Override for this class
                        </button>
                      )}
                    </div>
                  )
                ) : null}
              </Card>
            </div>
          </>
        )}
      </QueryBoundary>

      {toast && <Toast message={toast.message} type={toast.type} onDismiss={() => setToast(null)} />}
    </div>
  );
}
